import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  CloseWorkplaceRequestDto,
  SaveWorkplaceRequestDto,
  WorkplaceBlockingReferenceDto,
  WorkplaceDetailsDto,
  WorkplaceListDetailsDto,
} from './organisationApiTypes';

const base = (businessRecordId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/workplaces`;

// AC-005: a close refused for blocking references carries the structured list
// in the failure body's Result, not just a message — the panel needs the list
// to render each blocker's kind/id/label, so this is a distinct error type
// rather than a plain Error the generic parse() below would collapse to a string.
export class WorkplaceCloseBlockedError extends Error {
  constructor(message: string, public readonly blockingReferences: WorkplaceBlockingReferenceDto[]) {
    super(message);
  }
}

// The body is read even on an error status, because business refusals carry
// their operator-facing message in the JSON envelope. An unhandled server
// error is not JSON at all — ASP.NET returns a plain-text exception page —
// and parsing it used to throw first, hiding the status behind a JSON syntax
// error. Fall back to the status instead.
const parse = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => null) as ApiResponse<T> | null;
  if (!response.ok) throw new Error(body?.message || `Request failed: ${response.status}`);
  if (!body?.success || body.result == null)
    throw new Error(body?.message || 'The server returned an unreadable Workplaces response.');
  return body.result;
};

const json = (method: 'PUT' | 'POST', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const getWorkplaces = async (businessRecordId: string): Promise<WorkplaceListDetailsDto> =>
  parse(await authFetch(base(businessRecordId)));

export const createWorkplace = async (
  businessRecordId: string,
  request: SaveWorkplaceRequestDto,
): Promise<WorkplaceDetailsDto> =>
  parse(await authFetch(base(businessRecordId), json('POST', request)));

export const updateWorkplace = async (
  businessRecordId: string,
  workplaceId: string,
  request: SaveWorkplaceRequestDto,
): Promise<WorkplaceDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/${encodeURIComponent(workplaceId)}`, json('PUT', request)));

export const closeWorkplace = async (
  businessRecordId: string,
  workplaceId: string,
  request: CloseWorkplaceRequestDto,
): Promise<WorkplaceDetailsDto> => {
  const response = await authFetch(`${base(businessRecordId)}/${encodeURIComponent(workplaceId)}/close`, json('POST', request));
  const body = await response.json().catch(() => null) as ApiResponse<WorkplaceDetailsDto> | null;
  if (!response.ok) {
    const blockingReferences = body?.result?.blockingReferences;
    if (blockingReferences && blockingReferences.length > 0)
      throw new WorkplaceCloseBlockedError(body?.message || 'This Workplace cannot be closed.', blockingReferences);
    throw new Error(body?.message || `Request failed: ${response.status}`);
  }
  if (!body?.success || body.result == null)
    throw new Error(body?.message || 'The server returned an unreadable Workplaces response.');
  return body.result;
};
